/**
 * Phase1WindowControl.tsx — header cluster for the Phase-1 machine grid's date
 * window (BUG-2026-0178). Shows the visible range and lets the planner grow it
 * in either direction, as many times as needed, plus a "Fit to tasks" reset
 * once extended. Pure controlled component — state lives in usePhase1Window.
 *
 *   ◀◀  2026-08-05 → 2026-10-02  ▶▶   [Fit to tasks]
 */

import React from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { PHASE1_WINDOW_EXTEND_DAYS } from './usePhase1Window';

const EXTEND_WEEKS = PHASE1_WINDOW_EXTEND_DAYS / 7;

interface Phase1WindowControlProps {
  windowStart: string;
  windowEnd: string;
  /** True when the window has been grown past the tasks' span (shows the reset). */
  isExtended: boolean;
  /** Grow the window by PHASE1_WINDOW_EXTEND_DAYS: -1 = earlier, +1 = later. */
  onExtend: (dir: -1 | 1) => void;
  /** Snap back to the tasks' span. */
  onResetToFit: () => void;
  /** Bookings for the window are loading — spinner next to the range. */
  loading?: boolean;
}

export const Phase1WindowControl: React.FC<Phase1WindowControlProps> = ({
  windowStart,
  windowEnd,
  isExtended,
  onExtend,
  onResetToFit,
  loading = false,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
    <Tooltip title={`Show ${EXTEND_WEEKS} more weeks before (the grid's ◀ arrow does this too at the left edge)`}>
      <IconButton
        size="small"
        aria-label="Show earlier weeks"
        onClick={() => onExtend(-1)}
      >
        <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>

    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: '0.7rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      aria-label="Visible date window"
    >
      {windowStart} → {windowEnd}
      {loading && <CircularProgress size={10} sx={{ ml: 1, verticalAlign: 'middle' }} />}
    </Typography>

    <Tooltip title={`Show ${EXTEND_WEEKS} more weeks after (the grid's ▶ arrow does this too at the right edge)`}>
      <IconButton
        size="small"
        aria-label="Show later weeks"
        onClick={() => onExtend(1)}
      >
        <KeyboardDoubleArrowRightIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>

    {isExtended && (
      <Tooltip title="Shrink the window back to this plan's task dates">
        <Button
          size="small"
          variant="text"
          startIcon={<FitScreenIcon sx={{ fontSize: 14 }} />}
          onClick={onResetToFit}
          sx={{ ml: 0.5, fontSize: '0.68rem', textTransform: 'none', py: 0, minHeight: 0 }}
        >
          Fit to tasks
        </Button>
      </Tooltip>
    )}
  </Box>
);
